import Button from "@mui/material/Button";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  changeLanguage,
  LanguageType,
  selectCurrentLanguage,
  selectLanguages,
} from "app/store/i18nSlice";
import { useAppDispatch, useAppSelector } from "app/store/hooks";
import { selectUser } from "src/app/auth/user/store/userSlice";
import { useEffect } from "react";
import { usePropelUser } from "src/api/propel/userInstance";

/**
 * The language switcher.
 */
function LanguageSwitcher() {
  //set the initial language to
  const user = useAppSelector(selectUser);
  const initialLanguage = user.properties.chosen_language;

  const currentLanguage = useAppSelector(selectCurrentLanguage);
  const languages = useAppSelector(selectLanguages);
  const [menu, setMenu] = useState<null | HTMLElement>(null);
  const dispatch = useAppDispatch();
  const propelAxios = usePropelUser();

  // Use useEffect to set the initial language on component mount
  useEffect(() => {
    if (initialLanguage && currentLanguage.id !== initialLanguage) {
      dispatch(changeLanguage(initialLanguage));
    }
  }, []);

  const langMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenu(event.currentTarget);
  };

  const langMenuClose = () => {
    setMenu(null);
  };

  async function handleLanguageChange(lng: LanguageType) {
    // Update the app language immediately
    dispatch(changeLanguage(lng.id));

    // Save the language preference to the user's profile
    try {
      const userId = user.uid;
      await propelAxios.put(`/user/${userId}`, {
        properties: {
          chosen_language: lng.id,
        },
      });
      console.log("Language preference saved successfully");
    } catch (error) {
      console.error("Error saving language preference:", error);
      // Don't show error to user as the language change still works locally
    }

    langMenuClose();
  }

  return (
    <>
      <Button className="h-40 w-64" onClick={langMenuClick}>
        <img
          className="mx-4 min-w-20 "
          src={`assets/images/flags/${currentLanguage.flag}.svg`}
          alt={currentLanguage.title}
        />

        <Typography
          className="mx-4 font-semibold uppercase"
          color="text.secondary"
        >
          {currentLanguage.id}
        </Typography>
      </Button>

      <Popover
        open={Boolean(menu)}
        anchorEl={menu}
        onClose={langMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        classes={{
          paper: "py-8",
        }}
      >
        {languages.map((lng) => (
          <MenuItem key={lng.id} onClick={() => handleLanguageChange(lng)}>
            <ListItemIcon className="min-w-40">
              <img
                className="min-w-20 max-w-20"
                src={`assets/images/flags/${lng.flag}.svg`}
                alt={lng.title}
              />
            </ListItemIcon>
            <ListItemText primary={lng.title} />
          </MenuItem>
        ))}
      </Popover>
    </>
  );
}

export default LanguageSwitcher;
